import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { User, Mail, Phone, Check, AlertTriangle, FileDown, AlertCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useTrafficFines, TrafficFine, getDaysRemaining } from '@/hooks/useTrafficFines';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface DriverDesignationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fine: TrafficFine;
  isEditing?: boolean;
}

interface Driver {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string | null;
  license_number?: string | null;
  // CERFA required fields
  gender?: string | null;
  birth_date?: string | null;
  birth_place?: string | null;
  address_number?: string | null;
  address_street?: string | null;
  postal_code?: string | null;
  city?: string | null;
}

// Check if driver has all required CERFA fields
function hasCompleteCerfaInfo(driver: Driver): boolean {
  return !!(
    driver.last_name &&
    driver.first_name &&
    driver.gender &&
    driver.birth_date &&
    driver.birth_place &&
    driver.address_number &&
    driver.address_street &&
    driver.postal_code &&
    driver.city &&
    driver.license_number
  );
}

// Get missing fields for CERFA
function getMissingCerfaFields(driver: Driver): string[] {
  const missing: string[] = [];
  if (!driver.gender) missing.push('Sexe');
  if (!driver.birth_date) missing.push('Date de naissance');
  if (!driver.birth_place) missing.push('Lieu de naissance');
  if (!driver.address_number || !driver.address_street) missing.push('Adresse');
  if (!driver.postal_code) missing.push('Code postal');
  if (!driver.city) missing.push('Ville');
  if (!driver.license_number) missing.push('N° de permis');
  return missing;
}

export function DriverDesignationDialog({ open, onOpenChange, fine, isEditing = false }: DriverDesignationDialogProps) {
  const { user } = useAuth();
  const { designateDriver, changeDriver } = useTrafficFines();
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generateCerfa, setGenerateCerfa] = useState(!isEditing); // Disable CERFA by default when editing
  const { data: drivers = [], isLoading } = useQuery({
    queryKey: ['drivers-for-designation', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('drivers')
        .select('id, first_name, last_name, email, phone, license_number, gender, birth_date, birth_place, address_number, address_street, postal_code, city')
        .eq('user_id', user.id)
        .order('last_name');
      
      if (error) throw error;
      return data as Driver[];
    },
    enabled: !!user?.id && open,
  });

  const selectedDriver = drivers.find(d => d.id === selectedDriverId);
  const daysRemaining = getDaysRemaining(fine.designation_deadline);
  const isUrgent = daysRemaining !== null && daysRemaining <= 7;
  const canGenerateCerfa = selectedDriver && hasCompleteCerfaInfo(selectedDriver);
  const missingFields = selectedDriver ? getMissingCerfaFields(selectedDriver) : [];

  const handleDesignate = async () => {
    if (!selectedDriverId) return;

    setIsSubmitting(true);
    try {
      if (isEditing) {
        // Update existing designation
        const previousDriverName = fine.driver 
          ? `${fine.driver.first_name} ${fine.driver.last_name}` 
          : undefined;
        await changeDriver.mutateAsync({ 
          fineId: fine.id, 
          driverId: selectedDriverId,
          previousDriverName 
        });
      } else {
        // New designation
        await designateDriver.mutateAsync({ fineId: fine.id, driverId: selectedDriverId });
      }
      
      // Generate CERFA if requested and driver has complete info
      if (generateCerfa && canGenerateCerfa) {
        try {
          const { data, error } = await supabase.functions.invoke('fill-designation-form', {
            body: { fineId: fine.id, driverId: selectedDriverId }
          });

          if (error) throw error;

          if (data?.pdf) {
            // Download the PDF
            const byteCharacters = atob(data.pdf);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            
            const link = document.createElement('a');
            link.href = url;
            link.download = data.filename || 'designation.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            toast.success('Formulaire CERFA téléchargé', {
              description: 'Imprimez-le, signez-le et envoyez-le à l\'adresse indiquée sur l\'avis.'
            });
          }
        } catch (pdfError) {
          console.error('PDF generation error:', pdfError);
          toast.error('Erreur lors de la génération du formulaire CERFA');
        }
      }
      
      onOpenChange(false);
    } catch (error) {
      console.error('Designation error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {isEditing ? 'Changer de conducteur' : 'Désigner le conducteur'}
          </DialogTitle>
          <DialogDescription>
            <span className="font-mono">{fine.license_plate}</span>
            {fine.offense_date && ` • ${new Date(fine.offense_date).toLocaleDateString('fr-FR')}`}
            {isEditing && fine.driver && (
              <span className="block mt-1">
                Conducteur actuel : {fine.driver.first_name} {fine.driver.last_name}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Urgency warning */}
        {isUrgent && daysRemaining !== null && (
          <div className={cn(
            "flex items-center gap-2 p-3 rounded-lg",
            daysRemaining <= 3 
              ? "bg-destructive/10 text-destructive" 
              : "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
          )}>
            <AlertTriangle className="h-5 w-5" />
            <span className="text-sm font-medium">
              {daysRemaining > 0 
                ? `Plus que ${daysRemaining} jour${daysRemaining > 1 ? 's' : ''} pour désigner !`
                : daysRemaining === 0 
                  ? "Dernier jour pour désigner !"
                  : "Délai dépassé - Risque d'amende 750€"
              }
            </span>
          </div>
        )}

        {/* Drivers list */}
        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {isLoading ? (
            <div className="py-8 text-center">
              <Spinner className="h-6 w-6 mx-auto" />
            </div>
          ) : drivers.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              <User className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>Aucun conducteur enregistré</p>
              <p className="text-xs mt-1">Ajoutez des conducteurs dans la section Flotte</p>
            </div>
          ) : (
            drivers.map((driver) => (
              <Card
                key={driver.id}
                className={cn(
                  "cursor-pointer transition-all",
                  selectedDriverId === driver.id 
                    ? "border-primary bg-primary/5 ring-1 ring-primary" 
                    : "hover:border-muted-foreground/50"
                )}
                onClick={() => setSelectedDriverId(driver.id)}
              >
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                      <User className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {driver.first_name} {driver.last_name}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {driver.email}
                        </span>
                        {driver.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {driver.phone}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {selectedDriverId === driver.id && (
                    <Badge className="bg-primary">
                      <Check className="h-3 w-3" />
                    </Badge>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* CERFA generation option */}
        {selectedDriverId && (
          <div className="space-y-2">
            <div className="flex items-center space-x-2 p-3 rounded-lg bg-muted/50">
              <Checkbox
                id="generate-cerfa"
                checked={generateCerfa}
                onCheckedChange={(checked) => setGenerateCerfa(checked === true)}
              />
              <Label htmlFor="generate-cerfa" className="text-sm cursor-pointer flex items-center gap-2">
                <FileDown className="h-4 w-4" />
                Générer le formulaire CERFA de désignation
              </Label>
            </div>

            {generateCerfa && !canGenerateCerfa && missingFields.length > 0 && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium">Informations manquantes pour le CERFA :</p>
                  <p className="text-xs mt-1">{missingFields.join(', ')}</p>
                  <p className="text-xs mt-1 text-muted-foreground">
                    Modifiez le conducteur dans la section Flotte pour compléter son profil.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button 
            onClick={handleDesignate} 
            disabled={!selectedDriverId || isSubmitting || (isEditing && selectedDriverId === fine.driver_id)}
          >
            {isSubmitting ? <Spinner className="h-4 w-4 mr-2" /> : null}
            {isEditing ? 'Confirmer le changement' : 'Confirmer la désignation'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
